import { TOOLBAR_COMBO_SELECTOR } from './selectors';

/**
 * Pure logic for Excel-style single-stage KeyTips on the editor toolbar: one
 * Alt/⌥ tap badges every toolbar control at once (no tab stage), DOM target
 * resolution, the prefix filtering/auto-coding, and the Alt-tap arm/disarm/
 * toggle state machine. `useKeyTips.ts` wires this to the DOM (document
 * listeners, the vendor locale, focus restore); nothing here touches
 * `document`/React.
 */

/**
 * One flat, prefix-free code per toolbar control (single stage — a bare Alt
 * tap badges every one of these at once). Home's own controls keep their
 * original letters (1/2/3/4, B, H, FC, FF, FS, FP, EF, N, P, 9, 0, M, A, V, W,
 * FQ, L, US, FDF, FDS, OI, OE, Z, Y) unchanged; four controls that used to
 * live under a separate tab collided with a Home code once flattened into one
 * table and were reassigned the nearest free Excel-like code instead:
 *  - `image` was `P` (collides with `percentage-format`'s `P`) -> `G`.
 *  - `dataVerification` was `VV` (its leading `V` collides with
 *    `vertical-align`'s complete code `V`) -> `DV`.
 *  - `splitColumn` was `E` (collides with `clear-format`'s `EF`, which starts
 *    with `E`) -> `SP`.
 *  - `freeze` was `F` (collides with every `F*` Home code — `FC`/`FF`/`FS`/
 *    `FP`/`FQ`/`FDF`/`FDS`) -> `FZ`.
 * `link`, `screenshot`, `filter` and `comment` kept their original letters
 * (`I`, `SC`, `T`, `C`) — none collided once merged.
 */
export const KEYTIP_MAP: Readonly<Record<string, string>> = Object.freeze({
    // Home
    bold: '1',
    italic: '2',
    underline: '3',
    'strike-through': '4',
    undo: 'Z',
    redo: 'Y',
    'format-painter': 'FP',
    'clear-format': 'EF',
    format: 'N',
    'percentage-format': 'P',
    'number-decrease': '9',
    'number-increase': '0',
    font: 'FF',
    'font-size': 'FS',
    'font-color': 'FC',
    background: 'H',
    border: 'B',
    'merge-cell': 'M',
    'horizontal-align': 'A',
    'vertical-align': 'V',
    'text-wrap': 'W',
    'text-rotation': 'FQ',
    conditionFormat: 'L',
    'quick-formula': 'US',
    search: 'FDF',
    locationCondition: 'FDS',
    'fit-columns': 'OI',
    'format-cells': 'OE',
    // Formerly Insert
    link: 'I',
    image: 'G', // was 'P' — reassigned, see doc comment above
    screenshot: 'SC',
    // Formerly Data
    filter: 'T',
    dataVerification: 'DV', // was 'VV' — reassigned
    splitColumn: 'SP', // was 'E' — reassigned
    // Formerly Review
    comment: 'C',
    // Formerly View
    freeze: 'FZ', // was 'F' — reassigned
});

/** Every toolbar-item key the tooltip-lookup builder (`useKeyTips.ts`) needs
 *  to resolve a tooltip for. */
export const ALL_TOOLBAR_KEYS: readonly string[] = Object.keys(KEYTIP_MAP);

/** The More toggle's own pseudo-code — never a member of `KEYTIP_MAP` (it
 *  needs no toolbar control of its own), reserved only when at least one
 *  member control is actually overflowed. */
export const MORE_CODE = '.';

export interface ToolbarTarget {
    key: string;
    code: string;
    el: HTMLElement;
    /** `button`/`combo-open` are real, currently-visible toolbar controls —
     *  badged at their own rect. `menu-option` is a combo popup's own
     *  `.fortune-toolbar-select-option` row (packed with no gap between
     *  rows) — badged differently from a plain toolbar-row control by
     *  `KeyTipsOverlay`. `overflow` is a member control currently wrapped
     *  into the "More" panel at this width — `el` is the More toggle button
     *  itself; it gets NO badge of its own (nothing to point at yet), but
     *  stays typeable by its own code, which opens "More" then activates the
     *  real control once resolved. `more` is the single pseudo-target for
     *  the More toggle button itself (code `MORE_CODE`) — badged like any
     *  other real control; activating it opens the panel and badges the
     *  controls inside it at their real positions. */
    kind: 'button' | 'combo-open' | 'menu-option' | 'overflow' | 'more';
}

const isVisible = (el: HTMLElement): boolean => el.offsetParent !== null;

// SVGIcon always renders `<use xlinkHref="#{name}">` (react:339), and a
// default (non-overridden) toolbar button's `iconId` is always its own name
// (react:6660) — so any button whose tooltip we can't resolve is still
// findable by its icon. Confirmed necessary for 'search' and 'link': neither
// has a `toolbar.<name>` locale entry (no special-case override exists for
// them either), so their `data-tips` is never rendered.
const XLINK_NS = 'http://www.w3.org/1999/xlink';
const iconHrefOf = (use: Element): string | null =>
    use.getAttributeNS(XLINK_NS, 'href') ?? use.getAttribute('xlink:href') ?? use.getAttribute('href');

const findByIconName = (toolbarEl: HTMLElement, name: string): HTMLElement | null => {
    const match = Array.from(toolbarEl.querySelectorAll('use')).find((use) => iconHrefOf(use) === `#${name}`);
    return match ? (match.closest('[role="button"]') as HTMLElement | null) : null;
};

/** A 2-letter code that is not, and is not a prefix of (nor prefixed by), any
 *  code in `used`. The first letter is always one with no 1-letter code of
 *  its own, so no existing or future single-letter code can collide with it. */
const autoCode = (used: ReadonlySet<string>): string => {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    for (const first of letters) {
        if (used.has(first)) continue;
        for (const second of `${letters}0123456789`) {
            const candidate = first + second;
            if (!used.has(candidate)) return candidate;
        }
    }
    throw new Error('KeyTips: auto-code space exhausted');
};

/**
 * Resolves every visible toolbar control to a KeyTip target, across the
 * single flat `KEYTIP_MAP`. Iterates the DOM (not the map), so a member
 * control whose tooltip is unmapped/unrecognised still gets an auto-assigned
 * code instead of silently having no KeyTip. A dropdown always renders BOTH a
 * `.fortune-toolbar-combo-button` and a `.fortune-toolbar-combo-arrow`
 * sharing one `data-tips` (react:4247-4302) — resolved as ONE target,
 * preferring the arrow (it always opens the popup; the button may instead
 * apply the last-picked value).
 *
 * A member control that has wrapped into the "More" overflow at this width
 * gets a `kind: 'overflow'` target anchored to the More button (no badge of
 * its own — `useKeyTips`'s `activate` re-resolves the real control only once
 * the panel is open) plus, once per call, a single `kind: 'more'` target for
 * the More button itself. Calling this again against the OPENED
 * `.fortune-toolbar-more-container` (a small subtree with only the
 * overflowed controls, and no "More" button of its own) resolves each of
 * those controls as an ordinary `button`/`combo-open` target at its real
 * position — the same function serves both the main row and the open panel.
 */
export const resolveToolbarTargets = (toolbarEl: HTMLElement, tooltipsByKey: Record<string, string>): ToolbarTarget[] => {
    const memberKeys = new Set(Object.keys(KEYTIP_MAP));

    const tooltipToKey = new Map<string, string>();
    Object.entries(tooltipsByKey).forEach(([key, tooltip]) => tooltipToKey.set(tooltip, key));

    const usedCodes = new Set(Object.values(KEYTIP_MAP));
    const seenEls = new Set<Element>();
    const targets: ToolbarTarget[] = [];

    // `key`'s own KEYTIP_MAP code, or an auto-assigned one for a member with
    // none (shouldn't happen for a real key today, but mirrors the map's own
    // defensive fallback) — reserved against `usedCodes` either way.
    const codeFor = (key: string): string => {
        const code = KEYTIP_MAP[key] ?? autoCode(usedCodes);
        usedCodes.add(code);
        return code;
    };

    const addTarget = (el: HTMLElement, kind: ToolbarTarget['kind'], keyHint?: string): void => {
        if (!isVisible(el) || seenEls.has(el)) return;
        const tooltip = el.getAttribute('data-tips');
        const key = keyHint ?? (tooltip ? tooltipToKey.get(tooltip) : undefined);
        if (!key || !memberKeys.has(key)) return; // not a declared toolbar member (e.g. the global "More" button itself)
        seenEls.add(el);
        targets.push({ key, code: codeFor(key), el, kind });
    };

    // A control with NO `data-tips` at all (the 'search'/'link' vendor gap) is
    // left unresolved here rather than auto-coded as "unknown" — the icon
    // fallback pass below gets first refusal, since we DO know its real key.
    toolbarEl.querySelectorAll<HTMLElement>(TOOLBAR_COMBO_SELECTOR).forEach((combo) => {
        const arrow = combo.querySelector<HTMLElement>(':scope > .fortune-toolbar-combo-arrow');
        const button = combo.querySelector<HTMLElement>(':scope > .fortune-toolbar-combo-button');
        const el = arrow ?? button;
        if (el?.getAttribute('data-tips')) addTarget(el, 'combo-open');
    });

    toolbarEl.querySelectorAll<HTMLElement>('.fortune-toolbar-button').forEach((el) => {
        if (el.getAttribute('data-tips') && !el.closest(TOOLBAR_COMBO_SELECTOR)) addTarget(el, 'button');
    });

    const resolvedKeys = new Set(targets.map((t) => t.key));
    Object.keys(KEYTIP_MAP).forEach((key) => {
        if (resolvedKeys.has(key)) return;
        const el = findByIconName(toolbarEl, key);
        if (el) addTarget(el, 'button', key);
    });

    // Every member key still unresolved has wrapped into the "More" overflow
    // at this width — reserve its code against the More button so it stays
    // typeable (no badge of its own), and reserve the More button's own
    // pseudo-code once so it CAN be badged.
    const moreEl = findByIconName(toolbarEl, 'more');
    if (moreEl && isVisible(moreEl)) {
        const resolvedAfterFallback = new Set(targets.map((t) => t.key));
        let anyOverflowed = false;
        memberKeys.forEach((key) => {
            if (resolvedAfterFallback.has(key)) return;
            anyOverflowed = true;
            targets.push({ key, code: codeFor(key), el: moreEl, kind: 'overflow' });
        });
        if (anyOverflowed) targets.push({ key: 'more', code: MORE_CODE, el: moreEl, kind: 'more' });
    }

    return targets;
};

export const filterByPrefix = (targets: ToolbarTarget[], typed: string): ToolbarTarget[] => targets.filter((t) => t.code.startsWith(typed));

/** Codes for a nested popup's options, in DOM order: `1`-`9`, then `A`-`Z` —
 *  the generic fallback `resolveMenuOptions` uses for any combo whose options
 *  aren't icon-recognised (font size, number format, borders, fill, …). */
export const nestedCodes = (n: number): string[] => '123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'.slice(0, n).split('');

/** Icon href -> KeyTip letter for the align combos' own popup options
 *  (`react:5879-5940`, `Option > div.fortune-toolbar-menu-line > SVGIcon`) —
 *  icon-based, not locale-text-based, since `SVGIcon` renders a stable
 *  `#align-left`-style href regardless of locale. */
const ALIGN_OPTION_ICON_CODES: Readonly<Record<string, string>> = Object.freeze({
    'align-left': 'L',
    'align-center': 'C',
    'align-right': 'R',
    'align-top': 'T',
    'align-middle': 'M',
    'align-bottom': 'B',
});

/**
 * Resolves a combo popup's own option rows to KeyTip targets: the align
 * combos' options get Excel's own mnemonic letters (L/C/R/T/M/B, matched by
 * icon); every other combo (font size, number format, borders,
 * fill colour, merge, …) falls back to `nestedCodes`' generic DOM-order
 * numbering, exactly as before.
 */
export const resolveMenuOptions = (optionEls: HTMLElement[]): ToolbarTarget[] => {
    const iconCodes = optionEls.map((el) => {
        const use = el.querySelector('use');
        const href = use && iconHrefOf(use);
        const name = href?.replace('#', '');
        return name ? ALIGN_OPTION_ICON_CODES[name] ?? null : null;
    });
    const allRecognisedAndUnique = iconCodes.every((c): c is string => c != null) && new Set(iconCodes).size === iconCodes.length;
    const codes = allRecognisedAndUnique ? (iconCodes as string[]) : nestedCodes(optionEls.length);
    return optionEls.map((el, i) => ({ key: `nested-${i}`, code: codes[i], el, kind: 'menu-option' }));
};

// --- Alt-tap arm/disarm/toggle state machine ---

export interface AltTapState {
    armed: boolean;
}

export type AltTapEvent =
    | { type: 'keydown'; key: string; repeat: boolean; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }
    | { type: 'keyup'; key: string }
    | { type: 'mousedown' }
    | { type: 'blur' };

export type AltTapAction = 'arm' | 'disarm' | 'toggle' | 'none';

export const INITIAL_ALT_TAP_STATE: AltTapState = { armed: false };

/**
 * Pure: decides whether an event arms, disarms, or toggles KeyTips. A bare
 * Alt keydown (no other modifier, not a held-key repeat) arms; the matching
 * Alt keyup while still armed toggles; any other keydown, a mousedown, or a
 * blur disarms — which is what lets a chord like Alt+= or Alt+↓ fall through
 * to its own handler unimpeded (the chord's second keydown disarms before the
 * Alt keyup ever arrives). Contextual gates (editing, dialogs, target, `open`)
 * are not this reducer's concern — see `canArmKeyTips`.
 */
export const altTapReducer = (state: AltTapState, event: AltTapEvent): { state: AltTapState; action: AltTapAction } => {
    const disarmIfArmed = (): { state: AltTapState; action: AltTapAction } =>
        state.armed ? { state: { armed: false }, action: 'disarm' } : { state, action: 'none' };

    if (event.type === 'keydown') {
        if (event.key === 'Alt') {
            if (event.repeat) return { state, action: 'none' };
            if (!event.ctrlKey && !event.metaKey && !event.shiftKey) return { state: { armed: true }, action: 'arm' };
        }
        return disarmIfArmed();
    }
    if (event.type === 'keyup') {
        if (event.key === 'Alt' && state.armed) return { state: { armed: false }, action: 'toggle' };
        return disarmIfArmed();
    }
    return disarmIfArmed(); // mousedown / blur
};

export interface KeyTipsGateArgs {
    open: boolean;
    editing: boolean;
    nativeEditableTarget: boolean;
    dialogOpen: boolean;
    historyOpen: boolean;
    targetInsideEditor: boolean;
}

/** Whether an Alt tap is allowed to arm right now —
 *  checked by `useKeyTips` before ever feeding a keydown into `altTapReducer`. */
export const canArmKeyTips = (args: KeyTipsGateArgs): boolean =>
    args.open && !args.historyOpen && !args.dialogOpen && !args.editing && !args.nativeEditableTarget && args.targetInsideEditor;
